import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MdDelete, MdEdit } from 'react-icons/md';

import DepotMobile from '../DepotMobile';
import Layout from '../Layout';
import {
  creerObjet, listerContenu, modifierObjet, piecesDuContenu, supprimerObjet,
  supprimerPhoto, televerserPieceContenu, totalContenu,
} from '../api';

/** Ce qu'on peut joindre à un objet, et ce que ça prouve. */
const CATEGORIES = [
  { code: 'photo', libelle: 'Photo' },
  { code: 'facture', libelle: "Facture d'achat" },
  { code: 'devis', libelle: 'Devis de remplacement' },
];

const euros = (v) =>
  v === null || v === undefined
    ? '—'
    : `${Number(v).toLocaleString('fr-BE', { minimumFractionDigits: 2,
        maximumFractionDigits: 2 })} €`;

const VIDE = { item: '', description: '', date_buy: '', amount_buy: '',
               amount_ask: '' };

/**
 * Âge de l'objet au jour du sinistre.
 *
 * C'est lui qui commande la vétusté. L'afficher évite à l'assuré de découvrir
 * l'abattement dans le décompte de l'assureur.
 */
function anciennete(dateAchat) {
  if (!dateAchat) return null;
  const achat = new Date(dateAchat);
  if (Number.isNaN(achat.getTime())) return null;
  const mois = Math.max(0, Math.round(
    (Date.now() - achat.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));
  if (mois < 12) return `${mois} mois`;
  const annees = Math.floor(mois / 12);
  return `${annees} an${annees > 1 ? 's' : ''}`;
}

function Formulaire({ ouvert, valeurs, onChange, onFermer, onEnregistrer,
                     occupe, erreur }) {
  const champ = (nom, label, props = {}) => (
    <TextField
      label={label}
      value={valeurs[nom] ?? ''}
      onChange={(e) => onChange({ ...valeurs, [nom]: e.target.value })}
      fullWidth
      {...props}
    />
  );

  return (
    <Dialog open={ouvert} onClose={onFermer} fullWidth maxWidth="sm">
      <DialogTitle>
        {valeurs.id ? 'Modifier un objet' : 'Ajouter un objet'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erreur && <Alert severity="error">{erreur}</Alert>}

          {champ('item', 'De quel objet s’agit-il ?', {
            required: true,
            placeholder: 'Canapé trois places, lave-linge, ordinateur portable…',
          })}
          {champ('description', 'Précisions', {
            multiline: true, minRows: 2,
            helperText: 'Marque, modèle, état avant le sinistre. Plus vous êtes précis, moins la discussion sera longue.',
          })}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {champ('date_buy', 'Acheté le', {
              type: 'date', InputLabelProps: { shrink: true },
              helperText: "Une date approximative vaut mieux que rien.",
            })}
            {champ('amount_buy', 'Prix payé', {
              type: 'number',
              InputProps: { endAdornment: <InputAdornment position="end">€</InputAdornment> },
              helperText: 'Ce que vous aviez payé à l’époque.',
            })}
          </Stack>

          {champ('amount_ask', 'Ce que vous réclamez', {
            type: 'number',
            InputProps: { endAdornment: <InputAdornment position="end">€</InputAdornment> },
            helperText: "Le prix d'un équivalent aujourd'hui. Votre contrat dira si l'indemnité se calcule à neuf ou après vétusté.",
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFermer}>Annuler</Button>
        <Button variant="contained" onClick={onEnregistrer}
                disabled={occupe || !valeurs.item?.trim()}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Les pièces d'un objet : photo d'avant, facture, devis.
 *
 * Un objet daté et chiffré se défend ; un objet sans preuve se discute. C'est
 * la seule chose qui distingue une réclamation d'une affirmation.
 */
function Pieces({ expertiseId, objet, onQr }) {
  const [pieces, setPieces] = useState([]);
  const [categorie, setCategorie] = useState('photo');
  const [occupe, setOccupe] = useState(false);

  const recharger = useCallback(() => {
    piecesDuContenu(expertiseId, objet.id).then(setPieces).catch(() => {});
  }, [expertiseId, objet.id]);

  useEffect(() => { recharger(); }, [recharger]);

  const deposer = async (fichiers) => {
    setOccupe(true);
    try {
      for (const f of fichiers) {
        await televerserPieceContenu(expertiseId, objet.id, f, categorie);
      }
      recharger();
    } catch { /* l'écran reste utilisable, la pièce se redépose */ }
    finally { setOccupe(false); }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      {pieces.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap
               sx={{ mb: 1 }}>
          {pieces.map((p) => (
            <Chip key={p.id} size="small" variant="outlined"
                  label={`${p.category || 'pièce'} · ${p.title || ''}`.trim()}
                  onDelete={async () => {
                    await supprimerPhoto(p.id); recharger();
                  }} />
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap
             alignItems="center">
        <TextField select size="small" value={categorie}
                   onChange={(e) => setCategorie(e.target.value)}
                   sx={{ minWidth: 170 }} label="Type de pièce">
          {CATEGORIES.map((c) => (
            <MenuItem key={c.code} value={c.code}>{c.libelle}</MenuItem>
          ))}
        </TextField>
        <Button component="label" size="small" variant="outlined"
                disabled={occupe}>
          Joindre
          <input type="file" hidden multiple accept="image/*,application/pdf"
                 onChange={(e) => deposer([...e.target.files])} />
        </Button>
        <Button size="small" variant="outlined" disabled={occupe}
                onClick={() => onQr(objet, categorie)}>
          Avec mon téléphone
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * Le contenu : ce qui est dans le logement, pas le logement lui-même.
 *
 * Rien ici ne vient d'un barème de construction — un canapé n'a pas de prix au
 * mètre carré. L'assuré déclare ce qu'il avait, ce qu'il a payé et ce qu'il
 * réclame ; nous lui montrons l'âge de chaque objet, parce que c'est lui qui
 * commande la vétusté.
 */
export default function Contenu({ expertiseId, onSuivant, onRetour, onEtape }) {
  const [objets, setObjets] = useState(null);
  const [total, setTotal] = useState(null);
  const [erreur, setErreur] = useState('');
  const [erreurForm, setErreurForm] = useState('');
  const [edition, setEdition] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [qr, setQr] = useState(null);

  const recharger = useCallback(() => {
    Promise.all([listerContenu(expertiseId), totalContenu(expertiseId)])
      .then(([liste, t]) => { setObjets(liste); setTotal(t); })
      .catch(() => setErreur('Impossible de charger votre inventaire.'));
  }, [expertiseId]);

  useEffect(() => { recharger(); }, [recharger]);

  const enregistrer = async () => {
    setOccupe(true);
    setErreurForm('');
    try {
      if (edition.id) await modifierObjet(edition.id, edition);
      else await creerObjet(expertiseId, edition);
      setEdition(null);
      recharger();
    } catch {
      setErreurForm("L'enregistrement n'a pas abouti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async (objet) => {
    setErreur('');
    try {
      await supprimerObjet(objet.id);
      recharger();
    } catch {
      setErreur("La suppression n'a pas abouti.");
    }
  };

  return (
    <Layout
      etape="contenu"
      titre="Votre contenu"
      introduction="Les objets abîmés ou détruits : mobilier, électroménager, effets personnels. Le bâtiment se chiffre au barème, le contenu se déclare objet par objet."
      onEtape={onEtape}
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button variant="contained" size="large" onClick={onSuivant}>
            Continuer
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}
      {objets === null && !erreur && <CircularProgress size={28} />}

      <Stack spacing={2}>
        {objets?.map((o) => {
          const age = anciennete(o.date_buy);
          return (
            <Paper key={o.id} variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {o.item}
                  </Typography>
                  {o.description && (
                    <Typography variant="body2" color="text.secondary">
                      {o.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}
                         flexWrap="wrap" useFlexGap>
                    {age && <Chip size="small" variant="outlined"
                                  label={`acheté il y a ${age}`} />}
                    {Number(o.amount_buy) > 0 && (
                      <Chip size="small" variant="outlined"
                            label={`payé ${euros(o.amount_buy)}`} />
                    )}
                    {Number(o.amount_propose) > 0 && (
                      <Chip size="small" color="info" variant="outlined"
                            label={`proposé ${euros(o.amount_propose)}`} />
                    )}
                  </Stack>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600,
                                                  whiteSpace: 'nowrap' }}>
                  {euros(o.amount_ask)}
                </Typography>
                <IconButton aria-label={`Modifier ${o.item}`}
                            onClick={() => setEdition({ ...VIDE, ...o,
                              date_buy: o.date_buy || '' })}>
                  <MdEdit />
                </IconButton>
                <IconButton aria-label={`Supprimer ${o.item}`}
                            onClick={() => supprimer(o)}>
                  <MdDelete />
                </IconButton>
              </Stack>

              <Pieces expertiseId={expertiseId} objet={o}
                      onQr={(objet, categorie) => setQr({ objet, categorie })} />
            </Paper>
          );
        })}

        {objets?.length === 0 && (
          <Alert severity="info">
            Aucun objet déclaré. Si le sinistre n'a touché que le bâtiment,
            passez à la suite.
          </Alert>
        )}

        {objets?.length > 0 && total && (
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1">
                {objets.length} objet{objets.length > 1 ? 's' : ''} déclaré
                {objets.length > 1 ? 's' : ''}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {euros(total.total_ask)}
              </Typography>
            </Stack>
            {Number(total.total) > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Ce qui vous est proposé
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {euros(total.total)}
                  </Typography>
                </Stack>
              </>
            )}
          </Paper>
        )}

        <Paper variant="outlined"
               sx={{ p: 3, borderStyle: 'dashed', textAlign: 'center' }}>
          <Button variant="contained"
                  onClick={() => { setErreurForm(''); setEdition({ ...VIDE }); }}>
            Ajouter un objet
          </Button>
        </Paper>

        <Alert severity="info">
          Gardez ce que vous pouvez : facture, ticket, photo, garantie, relevé
          bancaire. Un objet sans preuve se discute ; un objet daté et chiffré
          se défend.
        </Alert>
      </Stack>

      <DepotMobile
        ouvert={qr !== null}
        onFermer={() => setQr(null)}
        expertiseId={expertiseId}
        categorie={qr?.categorie || 'photo'}
        contenu={qr?.objet?.id}
      />

      <Formulaire
        ouvert={edition !== null}
        valeurs={edition || VIDE}
        onChange={setEdition}
        onFermer={() => setEdition(null)}
        onEnregistrer={enregistrer}
        occupe={occupe}
        erreur={erreurForm}
      />
    </Layout>
  );
}
